import ScheduleAvailabilityManager from "@esWebAPI/es/map/ScheduleAvailabilityManager";

import type { FeatureViewModel } from "../FeatureViewModel";
import type { AzureBaseMap } from "./AzureBaseMap";
import type Layer from "./Layer";
import type ScheduleModel from "../ScheduleModel";
import type { AzureEdit } from "./AzureEdit";
import type { ILayerView, LayerInfo } from "@esWebAPI/es/model/Layer";
import type {Schedule, Service} from "@esWebAPI/es/model/Schedule";
import type {IEventChannel} from "@esWebAPI/es/model/Models";

import dayjs from "dayjs";

const es = global.es;

const MAX_SELECT_LIMIT = 1000;

const DEFAULT_MAX_STOPS_DAY_SCHEDULE = 50;
const DEFAULT_MAX_STOPS_WEEK_SCHEDULE = 125;
const DEFAULT_MAX_STOPS_MULTI_WEEK_SCHEDULE = 300;

async function getStopsConfig(): Promise<[number, number, number]> {
    let stopsConfig: [number, number, number] = [DEFAULT_MAX_STOPS_DAY_SCHEDULE, DEFAULT_MAX_STOPS_WEEK_SCHEDULE, DEFAULT_MAX_STOPS_MULTI_WEEK_SCHEDULE];
    
    if (!es.isPublicAccess) {
        try {
            const tenantConfig = await es.api.tenant.getTenantConfig();
            stopsConfig = [tenantConfig.maxStopsDaySchedule, tenantConfig.maxStopsWeekSchedule, tenantConfig.maxStopsMultiWeekSchedule];
        } catch (e) {
            // ignore, just use the default
        }
    }

    return stopsConfig;
}

export interface IRoutingModule {
    getServicesObjRef(): Service[];
    isServiceBreak(service: Service): boolean;
    getEventChannel(): IEventChannel;
    isShiftActive(shiftId: string): boolean;
    getShortListColorHex(): string;
    getShortListColorServer(): number;
    getInactiveColorHex(): string;
    getInactiveColorServer(): number;
    getShiftColorHex(shiftId: string): string;
    getShiftColorServer(shiftId: string): number;

    util: any;
}

export class ScheduleModule implements IRoutingModule {
    private readonly baseMapModule: AzureBaseMap;
    private readonly layerModule: Layer;
    private readonly model: ScheduleModel;
    private readonly editModule: AzureEdit;
    private readonly featureViewModel: FeatureViewModel;
    private readonly eventChannel;
    private maxStopsDaySchedule: number;
    private maxStopsWeekSchedule: number;
    private maxStopsMultiWeekSchedule:number;
    private geoMapId: number;
    private addressPointLayerViewId: number | null;
    readonly scheduleAvailabilityManager: ScheduleAvailabilityManager | null;
    readonly util: ScheduleModuleUtil;

    readonly loadComplete: Promise<void>;

    /**
     * This module/controller provides high-level public data methods that perform operation on the schedule data model
     * and api module.
     *
     * @param baseMapModule
     * @param layerModule
     * @param model scheduleModel
     * @param editModule
     * @constructor
     */
    constructor(baseMapModule: AzureBaseMap, layerModule: Layer, model: ScheduleModel, editModule: AzureEdit) {
        this.baseMapModule = baseMapModule;
        this.layerModule = layerModule;
        this.model = model;
        this.editModule = editModule;

        this.featureViewModel = layerModule.getFeatureViewModel();

        this.eventChannel = es.util.createEventChannel({
            onServicesReloaded: 1,
            onRemoveService: 1,
            onAddService: 1,
            onEditService: 1,
            onColorChange: 1,
            onShiftChange: 1,
            onGenerateSchedule: 1,
            onClearSchedule: 1,
            onTimeFrameChange: 1,
        }, "ScheduleModule");

        this.loadComplete = Promise.all([
            getStopsConfig(),
            model.loadComplete
        ]).then(([[maxStopsDaySchedule, maxStopsWeekSchedule, maxStopsMultiWeekSchedule]]) => {
            this.maxStopsDaySchedule = maxStopsDaySchedule;
            this.maxStopsWeekSchedule = maxStopsWeekSchedule;
            this.maxStopsMultiWeekSchedule = maxStopsMultiWeekSchedule;

            return Promise.resolve();
        });

        this.geoMapId = layerModule.getFeatureViewModel().geoMapId;

        this.util = new ScheduleModuleUtil(this, model, baseMapModule, layerModule);

        if (!model.isScheduleGeoMap()) {
            this.addressPointLayerViewId = null;
            this.scheduleAvailabilityManager = null;

            return;
        }

        this.addressPointLayerViewId = this.featureViewModel.getLayersByType("MapScopeCustomPointLayerView")[0].getLayerView().id;
        this.scheduleAvailabilityManager = new ScheduleAvailabilityManager(model.getId());

        es.api.getEventChannel().addListener("onCreateLayerView", this.setActiveRecommendationForLayer, this);

        // Check if recommendations were created for datasets used in the map
        const layersWithNoActiveRecommendations = this.featureViewModel.listLayers().filter(l =>
            l.isSelectable()
            && l.getGeomType() === "POINT"
            && (!(l.getTitle() === "Address Points" && l.getType() === "MapScopeCustomPointLayerView"))
            && this.getActiveRecommendation(l.getId()) === undefined
        );

        layersWithNoActiveRecommendations.forEach(layerViewModel => {
            this.setActiveRecommendationForLayer(layerViewModel.getLayerView(), layerViewModel.getLayerInfo());
        });

        es.api.webSocket.addListener((ev) => {
            if (ev.$type != 'LayerViewRecommendationRemoved' || !layerModule.getLayerViewIds().includes(ev.layerViewId)) {
                return;
            }

            const lv = layerModule.getById(ev.layerViewId);
            model.removeActiveRecommendation(lv.getLayerMetaId());

            Promise.all([
                es.api.layerView.get(lv.getId(), {"noCache": true}),
                es.api.layerInfo.getForLayerView(lv.getId(), null, {"noCache": true}),
                es.api.geoMap.get(lv.getGeoMapId(), {"noCache": true}),
                (ev.updatedFSCriteria ? es.api.layerView.getFeatureSourceCriterias(ev.layerViewId, {"noCache": true}) : es.util.deferred.resolved())
            ]).then(([layerView, layerInfo, geoMap, fsCriteria]) => {
                es.api.getEventChannel().fireEvent("onUpdateGeoMap", geoMap);
                es.api.getEventChannel().fireEvent("onUpdateLayerView", layerView, layerInfo, geoMap.lastModified);
                if (ev.updatedFSCriteria) {
                    es.api.getEventChannel().fireEvent("onUpdateLayerViewRemovableCriteria", fsCriteria, layerView.id);
                }

                this.setActiveRecommendationForLayer(layerView, layerInfo);
            });
        });
        const scheduleId = model.getId();
        es.api.webSocket.addOpenListener(() => {
            es.api.webSocket.send({'$type': 'ListenScheduleChanges', 'scheduleId': scheduleId});
        });
    }

    /**
     * 
     * @returns 
     */
    getEventChannel() {
        return this.eventChannel;
    }

    /**
     * 
     * @returns 
     */
    getDefaultSettings() {
        return this.model.getDefaultSettings();
    }

    /**
     * 
     * @returns 
     */
    getSettingsObjRef() {
        return this.model.getSettingsObjRef();
    }

    /**
     * 
     * @returns 
     */
    getStopServicesObjRef(): any[] {
        return this.model.getStopServicesObjRef();
    }

    /**
     * 
     * @returns 
     */
    getSettingsClone() {
        return this.model.getSettingsClone();
    }

    /**
     * 
     * @returns 
     */
    getId(): number {
        return this.model.getId();
    }

    /**
     * 
     * @returns 
     */
    getServicesClone(): any[] {
        return this.model.getServicesClone();
    }

    /**
     * 
     * @returns 
     */
    getServicesWithFeature(): Promise<any[]> {
        const services = this.getServicesClone().filter(s => !this.isServiceBreak(s));

        const layerViewIds: number[] = _.uniq(services.map(s => s.layerViewId));

        const featureIdsPerLayer = {};

        return Promise.all(layerViewIds.map(lvId => {
            let featureIds = services.filter(s => s.layerViewId === lvId).map(s => s.featureId);
            featureIdsPerLayer[lvId] = featureIds;

            return Promise.all([
                es.api.layerView.getAttributesSpecialFunction(lvId),
                es.api.feature.getByIds(lvId, featureIds)
            ]).then(([attrInfo, features]) => {
                const services = features.map(f => {
                    let service = this.util.getServicesFromFeat(f)[0];
                    f.specialAttrs = attrInfo;
                    service.feature = f;

                    return service;
                });

                return services;
            });
        })).then((services) => {
            return services.flatMap(e => e);
        });
    }

    /**
     * 
     * @returns 
     */
    getServicesObjRef(): any[] {
        return this.model.getServicesObjRef();
    }

    /**
     * Returns a debug/toString version of services with timestamps etc converted to human-readable values
     * @param services optional. if not provided then returns services from current model
     * @returns {*}
     */
    getServices_debug(services: any[]) {
        return this.model.getServices_debug(services);
    }

    /**
     * 
     * @returns 
     */
    getShiftsClone() {
        return this.model.getShiftsClone();
    }

    /**
     * 
     * @returns 
     */
    getShiftsObjRef() {
        return this.model.getShiftsObjRef();
    }

    /**
     * 
     * @returns 
     */
    getShifts_debug() {
        return this.model.getShifts_debug();
    }

    /**
     * 
     * @param settings 
     * 
     * @returns 
     */
    getSettings_debug(settings) {
        return this.model.getSettings_debug(settings);
    }

    /**
     * 
     * @returns 
     */
    getTimeFrame_debug() {
        return this.model.getTimeFrame_debug();
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceScheduledInSolution(service) {
        return this.model.isServiceScheduledInSolution(service);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceUnScheduled(service) {
        return this.model.isServiceUnScheduled(service);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceScheduled(service) {
        return this.model.isServiceScheduled(service);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceScheduledOrHasStartTime(service) {
        return this.model.isServiceScheduledOrHasStartTime(service);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceLocked(service) {
        return this.model.isServiceLocked(service);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceBreak(service) {
        return this.model.isServiceBreak(service);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceAnyStartOrEnd(service) {
        return this.model.isServiceAnyStartOrEnd(service);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isUserService(service) {
        return this.model.isUserService(service);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceStartOnly(service) {
        return this.model.isServiceStartOnly(service);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceEndOnly(service) {
        return this.model.isServiceEndOnly(service);
    }

    /**
     * 
     * @returns 
     */
    hasSolution(): boolean {
        return this.model.hasSolution();
    }

    /**
     * 
     * @returns 
     */
    areSettingsLocked(): boolean {
        return this.model.areSettingsLocked();
    }

    /**
     * Get Max number of services that can be added.
     * 
     * @returns {int}
     */
    getNumRemainingServices(): number {
        return this.model.getNumRemainingServices();
    }

    /**
     * Get Max number of services for the current schedule Settings
     * 
     * @returns {int}
     */
    getServicesLimit(): number {
        return this.model.getServicesLimit();
    }

    /**
     * Is shiftId within the current model timeframe
     * 
     * @param shiftId
     * 
     * @returns {boolean}
     */
    isShiftActive(shiftId: string): boolean {
        return this.model.isShiftActive(shiftId);
    }

    /**
     * Set new timeframe state.
     * 
     * @param timeFrame Array of 2 Unix Timestamps (milliseconds).
     * @param isSuppressEvent
     * 
     * @returns true if a new timeframe was set, false if timeframe was the same as the existing one,
     *          in which case, this method doesn't do anything.
     */
    public setTimeFrame(timeFrame: number[], isSuppressEvent = false): boolean {
        if (!Array.isArray(timeFrame) || timeFrame.length !== 2 || (typeof timeFrame[0] !== "number") || (typeof timeFrame[1] !== "number")) {
            throw new Error("setTimeFrame : invalid timeFrame param passed: " + JSON.stringify(timeFrame));
        }

        if (es.util.object.equals(timeFrame, this.model.getTimeFrame())) {
            //es.log.log("skip setTimeFrame, same", timeFrame, model.getTimeFrame());
            return false;
        }

        this.model.setTimeFrame(timeFrame);

        if (!isSuppressEvent) {
            this.eventChannel.fireEvent("onTimeFrameChange", timeFrame);
        }

        return true;
    }

    /**
     * Get the current timeframe state.
     * 
     * @returns {number[]} Array of 2 Unix Timestamps (milliseconds).
     */
    getTimeFrame(): number[] {
        return this.model.getTimeFrame();
    }

    /**
     * Get the shift that represents a particular day
     * 
     * @param date Javascript Date Object
     * 
     * @returns {shift}
     */
    getShiftByDateClone(date: Date) {
        return es.util.object.deepCopy(this.getShiftByObjRef(date));
    }

    /**
     * Get the shift that represents a particular day
     * 
     * @param date Javascript Date Object
     * 
     * @returns {shift}
     */
    getShiftByObjRef(date: Date) {
        return this.model.getShiftByDay(date);
    }

    /**
     * 
     * @param serviceId 
     * 
     * @returns 
     */
    getServiceClone(serviceId: string) {
        if (!serviceId) {
            throw new Error("Invalid serviceId " + serviceId);
        }

        return es.util.object.deepCopy(this.model.getService(serviceId));
    }

    /**
     * Get services with explicitly set startTime. Note: may or may not have stopInfo (i.e. be "scheduled")
     * (from app PoV, finds the services with timewindows to display on calendar)
     */
    getServiceWithOverrideStartTimesClone(): any[] {
        return es.util.object.deepCopy(this.model.getServicesObjRef().filter(s => s.overrideStartTime));
    }

    /**
     *
     * @param feature must have props layerViewId and id
     * 
     * @returns {string}
     */
    getAddressLocationId(feature): string {
        return `${feature.layerViewId}_${feature.id}`;
    }

    /**
     * 
     * @param index 
     * @param color 
     */
    setColor(index: number, color: string) {
        this.model.setColor(index, es.util.color.serverFromHex(color));
        this.updateRoutesColor();
        this.persistSettingsModel();

        this.eventChannel.fireEvent("onColorChange", index);
    }

    /**
     * 
     * @param index 
     * 
     * @returns 
     */
    getColor(index: number): string {
        return es.util.color.serverToHex(this.model.getColor(index));
    }

    /**
     * 
     * @param index 
     * 
     * @returns 
     */
    getColorServer(index: number): number {
        return this.model.getColor(index);
    }

    /**
     * 
     * @param shiftId 
     * 
     * @returns 
     */
    getShiftColorHex(shiftId: string): string {
        return es.util.color.serverToHex(this.model.getShiftColor(shiftId));
    }

    /**
     * 
     * @param shiftId 
     * 
     * @returns 
     */
    getShiftColorServer(shiftId: string): number {
        return this.model.getShiftColor(shiftId);
    }

    /**
     * 
     * @returns 
     */
    getInactiveColorServer(): number {
        return this.model.getInactiveColor();
    }

    /**
     * 
     * @returns 
     */
    getInactiveColorHex(): string {
        return es.util.color.serverToHex(this.model.getInactiveColor());
    }

    /**
     * 
     * @returns 
     */
    getShortListColorServer(): number {
        return this.model.getShortListColor();
    }

    /**
     * 
     * @returns 
     */
    getShortListColorHex(): string {
        return es.util.color.serverToHex(this.model.getShortListColor());
    }

    /**
     * 
     * @returns 
     */
    getAddressPointLayerViewId(): number|null {
        return this.addressPointLayerViewId;
    }

    /**
     * 
     * @param serviceId 
     * @returns 
     */
    removeScheduleLayerViews(serviceId = -1): JQueryDeferred<void> {
        const dfds = [];

        this.featureViewModel.getLayersByType("ScheduleLayerView").forEach(lv => {
            if(serviceId === -1 || this.model.getService(serviceId)?.stopInfo?.shiftId === lv.getShiftId()) {
                this.featureViewModel.removeLayer(lv.getId()); // TODO: is this necessary? es.api.layerView.remove call this?
                dfds.push(
                    es.api.layerView.remove(lv.getId())
                );
            }
        });

        if (dfds.length == 0) {
            return es.util.deferred.resolved();
        }

        return $.when.apply($, dfds).then(function() {
            es.log.log("Removed associated route layerView(s) for", serviceId);
            return es.util.deferred.resolved();
        });
    }

    /**
     * 
     * @param shiftIds 
     * 
     * @returns 
     */
    removeScheduleLayerViewsByShiftId(shiftIds: string[]): JQueryDeferred<void> {
        const dfds = [];
        const layerViewsToRemove = this.featureViewModel.getLayersByType("ScheduleLayerView")
            .filter(lv => shiftIds.includes(lv.getShiftId()))
        
        this.featureViewModel.removeLayer(...layerViewsToRemove.map(lv => lv.getId()));

        layerViewsToRemove.forEach(lv => {
                dfds.push(
                    es.api.layerView.remove(lv.getId())
                );
        });

        if (dfds.length == 0) {
            return es.util.deferred.resolved();
        }

        return $.when.apply($, dfds).then(function() {
            es.log.log("Removed associated route layerView(s) for ", shiftIds);
            return es.util.deferred.resolved();
        });
    }

    /**
     * 
     * @param shiftId 
     * 
     * @returns 
     */
    getScheduleLayerViewByShiftId(shiftId: string) {
        return _.find(this.featureViewModel.listLayers().map(l => l.getLayerView()), lv => lv.$type === "ScheduleLayerView" && lv.shiftId === shiftId) ?? null;
    }

    /**
     * 
     * @param lvId 
     * @param featId 
     * 
     * @returns 
     */
    getScheduleLayerViewForServiceFeat(lvId: number, featId: number) {
        const service = this.model.getServicesObjRef().filter(s => s.layerViewId === lvId && s.featureId === featId);
        if (service.length === 1 && service[0].stopInfo != undefined) {
            return this.getScheduleLayerViewByShiftId(service[0].stopInfo.shiftId);
        }

        // No routeLv for this service
        return null;
    }

    /**
     * Adds a point to the map with given values and then adds a corresponding service to the scheduleModel.
     * Intended to be used to respond to change in the scheduleModel for adding an EVENT or SAVED_LOCATION ONLY.
     * Services for points on existing layers (incl. on addressPoint layer) should be added via the scheduleModel.
     *
     * Note: In the case of a savedLocation, we will not store a reference to the source/parent entity. An Event has
     *       no formal entity type at all. It only exists within the context of a schedule as a service.
     *
     * e.g.: esApp.map.schedule.addAddressPointService("Event 1", "Some Address, USA", [-119.1659, 46.2059]);
     *
     * @param name service name/title, also used as label NOTE: THIS SHOULD BE UNIQUE IF PASSING AN ARRAY OF DATA VIA #addAddressPointServices
     * @param addressStr Optional. Note this is an ADDRESS string attr as found in MapScopeCustomPointLayerView and NOT an Address obj as found in GH request.Address obj.
     * @param lonLat Optional. If not provided, NO_GEOM point will be created and service in model will still be created.
     * @param duration Optional. Default: 0. Specifies the duration of the service in seconds, i.e. how long it takes at the customer site.
     * @param isSuppressEvent
     * 
     * @returns {*|jQuery}
     *
     * @see ScheduleModel#addService
     */
    addAddressPointService(name: string, addressStr = "", lonLat, duration = 0, isSuppressEvent = false): JQueryDeferred<object[]> {
        return this.addAddressPointServices([{
                name: name,
                addressStr: addressStr,
                lonLat: lonLat,
                duration: duration
            }],
            isSuppressEvent);
    }

    /**
     * 
     * @param addPtSrv 
     * @param isSuppressEvent 
     * @param persist 
     * 
     * @returns 
     */
    addAddressPointServices(addPtSrv, isSuppressEvent = false, persist = true): JQueryDeferred<object[]> {
        const pointsToCheck = addPtSrv.map((point, index) => ({name: point.name, address: point.addressStr, lonLat: point.lonLat, index}));

        return this.util.getAddressPointsOnCoordinates(pointsToCheck).then(({matchedFeatures, unmatchedPoints}) => {
            const pointsToCreate = unmatchedPoints.map(ump => {
                const attrs = {
                    name: ump.name,
                    address: ump.address ?? "",
                };
                if (ump.lonLat) {
                    attrs.point = ump.lonLat;
                }
                return attrs;
            });

            const createdServices = [];

            const convertToService = (ptSrv, featureId) => {
                let address = null;
                if(Array.isArray(ptSrv["lonLat"])){
                    address = {
                        locationId: this.addressPointLayerViewId + "_" + featureId,
                        lon: ptSrv.lonLat[0],
                        lat: ptSrv.lonLat[1],
                        name: ptSrv.name
                    };
                }

                return this.model.addService(
                    this.addressPointLayerViewId,
                    featureId,
                    ptSrv.name,
                    ptSrv.duration,
                    address,
                    ptSrv.overrideStartTime ?? null,
                    ptSrv.stopInfo ?? null,
                    ptSrv.locked ?? false
                );
            }

            // Create services with the features already on the Address Points Layer

            addPtSrv.forEach((pointService, idx) => {
                const matchedFeature = matchedFeatures.get(idx);
                if(matchedFeature) {
                    createdServices.push(convertToService(pointService, matchedFeature.id));
                }
            });

            // If all the features are already on the map, no need to create points. Save the model and return
            if (pointsToCreate.length === 0){
                const dfd = persist ? this.persistServiceModel() : es.util.deferred.resolved();
                return dfd.then(() => {
                    if(!isSuppressEvent) {
                        this.eventChannel.fireEvent("onAddService", createdServices);
                    }
                    return createdServices;
                } );
            }

            return es.util.mapScope.createAddressPoints(this.addressPointLayerViewId, pointsToCreate, this.editModule).then((createResult) => {
                for (let [idx, pointService] of addPtSrv.entries()) {
                    if (matchedFeatures.get(idx)) {
                        continue;
                    }

                    const rs = _.find(createResult, rs => {
                        return pointService.name === rs.action.attrs.NAME && pointService.addressStr == rs.action.attrs.ADDRESS
                            && (rs.action.geom == undefined && pointService.lonLat == undefined || (pointService.lonLat != undefined && _.isEqual(rs.action.geom, ["PT", ...pointService.lonLat])))
                    });

                    if(!rs) {
                        return es.util.deferred.rejected("Could not find a point feat created for " + pointService.name);
                    }

                    createdServices.push(convertToService(pointService, rs.featureId));
                }

                return createdServices;
            }).then((createdServices) => {
                const dfd = persist ? this.persistServiceModel() : es.util.deferred.resolved();
                return dfd.then(() => createdServices);
            }).then((createdServices) => {
                const dfds = addPtSrv.filter(p => p.availability).map(p => {
                    const s = createdServices.find(s => s.name == p.name);
                    const editActions = [{
                        $type: "Update",
                        attrs: {ES_AVAILABILITY : JSON.stringify(p.availability)},
                        featureId: s.featureId,
                        layerViewId: s.layerViewId
                    }];

                    return es.api.feature.edit(editActions);
                });

                return Promise.all(dfds).then(() => createdServices);
            }).then((createdServices) => {
                if (!isSuppressEvent) {
                    this.eventChannel.fireEvent("onAddService", createdServices);
                }

                return createdServices;
            });
        });
    }

    /**
     * 
     * @param settings 
     * @param services 
     * @param previousDefaultDuration 
     * 
     * @returns 
     */
    private updateServicesDuration(settings, services: any[], previousDefaultDuration: number) {
        // Only update the duration of stops in the shortlist
        let servicesToFetch = services.filter(service =>
            !this.model.isServiceBreak(service)
            && !this.model.isServiceAnyStartOrEnd(service)
            && !service.overrideStartTime
            && !service.stopInfo
        );
        const internalServices = _.difference(services, servicesToFetch);

        if (servicesToFetch.length === 0) {
            return es.util.deferred.resolved(services);
        }

        const defaultStopDuration = settings.stopDuration;
        // Set the default stop duration for all services

        if (defaultStopDuration !== previousDefaultDuration) {
            for(const service of servicesToFetch) {
                const newDuration = service.duration === previousDefaultDuration ? defaultStopDuration : service.duration;
                service.duration = newDuration;
            }
        }

        const servicesPerLayerViewId = _.groupBy(servicesToFetch, (s) => +s.layerViewId);

        if (!settings['stopTimeFieldMapping'] || _.isEmpty(settings.stopTimeFieldMapping)) {
            return es.util.deferred.resolved([].concat(internalServices).concat(servicesToFetch));
        }

        const stopTimeLayerMetaIds = Object.keys(settings.stopTimeFieldMapping).map(id => +id);
        const dfds = [];
        const layerViewIds = [];

        this.featureViewModel.listLayers()
            .filter(layer => stopTimeLayerMetaIds.includes(layer.getLayerMetaId()))
            .forEach(layer => {
                const servicesToFetch = _.uniq(servicesPerLayerViewId[layer.getId()]);
                if(servicesToFetch.length === 0) {
                    return es.util.deferred.resolved([]);
                }

                const featureIdsToFetch = servicesToFetch.map(service => service.featureId);
                const attribute = settings.stopTimeFieldMapping[layer.getLayerMetaId()];
                const criteria = {$type:"In", attr:"ES_ID", values: featureIdsToFetch};
                dfds.push(es.api.feature.listLean(layer.getId(), [attribute], criteria));
                layerViewIds.push(layer.getId());
            });


        return Promise.all(dfds).then((args) => {
            const featuresPerLayer = {};

            for(let i=0; i < layerViewIds.length; i++) {
                featuresPerLayer[layerViewIds[i]] = args[i].reduce((ac, f) => ({...ac, [f.id]: f}), {});
            }

            for (let [layerViewId, services] of Object.entries(servicesPerLayerViewId)) {
                layerViewId = +layerViewId;
                const stopTimeAttribute = settings.stopTimeFieldMapping[this.featureViewModel.getLayerById(layerViewId).getLayerMetaId()];
                for (const s of services) {
                    const serviceFeatureId = s.featureId;
                    s.duration = featuresPerLayer[layerViewId]?.[serviceFeatureId]?.attrs?.[stopTimeAttribute];
                    s.duration = s.duration != null ? s.duration * 60 : defaultStopDuration; // minutes to seconds if loaded from feature
                }
            }
            return internalServices.concat(Object.values(servicesPerLayerViewId).flat());
        });
    }

    /**
     * 
     * @param settings 
     * 
     * @returns 
     */
    private generateShiftsFromSettings(settings): any[] {
        const shifts = [];
        const finishDate = dayjs.unix(settings.finishDate);

        let date = dayjs.unix(settings.startDate);
        let shiftId = 0;

        for (date; date <= finishDate; date = date.add(1, "day")) {
            // check for availability from day of the week
            if (this.util.isDayAvailable(date, settings.dayAvailability)) {
                const shift = {
                    shiftId: `shift_${++shiftId}`,
                    earliestStart: date.clone().startOf("day").add(settings.shiftStartTime, "second").unix(),
                    latestEnd: date.clone().startOf("day").add(settings.shiftFinishTime, "second").unix(),
                    locked: false,
                    returnToDepot: settings.endAddress != undefined
                }

                if(settings.startAddress) {
                    shift["startAddress"] = settings.startAddress;
                }

                if(settings.endAddress){
                    shift["endAddress"] = settings.endAddress;
                }
                shifts.push(shift);
            }
        }

        return shifts;
    }

    /**
     * 
     * @param settings 
     * @param shifts 
     */
    private generateStartEndServicesFromSettings(settings, shifts: any[]) {
        const services = [];

        // Remove old start/end services - EVEN IF THEY ARE SCHEDULES/LOCKED DAY
        const serviceIdsToRemove = this.model.getServicesObjRef().filter(s => this.model.isServiceAnyStartOrEnd(s)).map(s => s.id);
        serviceIdsToRemove.forEach(id => this.model.removeService(id));

        for (let shift of shifts) {
            if (settings.startAddress) {
                const split = settings.startAddress.locationId.split("_");
                services.push({
                    layerViewId: +split[0],
                    featureId: +split[1],
                    name: settings.startAddress.name,
                    duration: 0,
                    address : settings.startAddress,
                    locked: true,
                    stopInfo: {
                        shiftId: shift.shiftId,
                        type: "START",
                        arrTime: shift.earliestStart,
                        endTime: shift.earliestStart,
                        drivingTime: 0,
                        waitingTime: 0
                    }
                });
            }

            if (settings.endAddress) {
                const split = settings.endAddress.locationId.split("_");
                services.push({
                    layerViewId: +split[0],
                    featureId: +split[1],
                    name: settings.endAddress.name,
                    duration: 0,
                    address : settings.endAddress,
                    locked: true,
                    stopInfo: {
                        shiftId: shift.shiftId,
                        type: "END",
                        arrTime: shift.latestEnd,
                        endTime: shift.latestEnd,
                        drivingTime: 0,
                        waitingTime: 0
                    }
                });
            }
        }

        services.forEach(s => {
            this.model.addService(s.layerViewId, s.featureId, s.name, s.duration, s.address, s.overrideStartTime, s.stopInfo, true);
        });
    }

    /**
     * Generate a lunch break service for each day, based on the shifts.
     *
     * Note: this method creates points on mapscope layer and automatically adds services to the scheduleModel.
     *
     * @returns {*}
     */
    private generateBreaksFromShiftsAndSettings(settings, shifts: any[]) {
        const addrPointData = [];
        if (settings.breakDuration > 0) {
            shifts.forEach(function (shift) {
                let dayOfWeek = dayjs(new Date(shift.earliestStart * 1000)).day();
                if (dayOfWeek == 0) {
                    dayOfWeek == 7; // Sunday should be 7, not 0
                }

                const startTime = settings.breakEarliestStartTime;
                const duration = settings.breakDuration;
                const endTime = settings.breakLatestStartTime + duration*60; // latest to finish

                addrPointData.push({
                    name: "es_lunch-" + shift.shiftId,
                    addressStr: null,
                    lonLat: null,
                    duration: duration,
                    availability: [{
                        dayOfWeek: dayOfWeek,
                        startTime: startTime,
                        endTime: endTime
                    }]
                });
            });
        }

        es.log.log("generateBreaksFromShiftsAndSettings complete", addrPointData);
        return this.addAddressPointServices(addrPointData, true, false);
    }

    /**
     * High-level method that consolidates several actions that always take place when the settings are updated i.e.
     *  - Load the new settings into the model and persist them
     *  - Generate shifts from the settings dates and persist them
     *  - Generate lunch-break services from shifts and persist them
     *  - Generate additional start and end services from the shifts (if req'd) and persist them.
     *
     * NOTE: this action resets all shifts
     *
     * @param settings
     * 
     * @returns {*}
     */
    async updateScheduleSettingsAndPersist(settings) {
        // Store the current shifts informations keyd by the shift day.
        // we need this information to keep locked status and shift colours on the same day
        // regardless of the shift id
        const shiftByDay = new Map();
        for(const shift of this.model.getShiftsObjRef()) {
            const shiftDay  = dayjs.unix(shift.earliestStart).startOf("day").unix();
            shiftByDay.set(shiftDay, shift);
        }

        const newShifts = this.generateShiftsFromSettings(settings);
        // Keep the same locked state for the same days
        for(const newShift of newShifts) {
            const shiftDay  = dayjs.unix(newShift.earliestStart).startOf("day").unix();
            newShift.locked = shiftByDay.get(shiftDay)?.locked ?? false;
        }

        // Update the shiftId on the layerviews so it reflect the same day
        for(const layerView of this.featureViewModel.getLayersByType("ScheduleLayerView")) {
            const layerViewShiftId = layerView.getShiftId();
            const shiftDay = dayjs.unix(this.model.getShift(layerViewShiftId).earliestStart).startOf("day");
            const newShiftId = newShifts.find(s => dayjs.unix(s.earliestStart).isSame(shiftDay, "day")).shiftId;
            layerView.setShiftId(newShiftId);
        }

        this.model.clearShifts();
        this.model.addShifts(newShifts);

        // Iterate over all services to so clean up / fix stop info
        for (const service of this.model.getServicesObjRef()) {
            // Update services shift ids
            if (service.stopInfo && service.stopInfo.shiftId) {
                const startTime = service.stopInfo.arrTime;
                const shift = this.util.getShiftFromDate(startTime);
                if (!shift) {
                    es.log.warn("There is a service scheduled that doesn't match any shift", service);
                    continue;
                }
                service.stopInfo.shiftId = shift.shiftId;
                continue;
            }

            // Remove old breaks & start/end service
            // We remove check this after checking the shift id because we don't want to remove
            // break services that are scheduled for a particular day
            if (service.stopInfo && service.stopInfo.type !== "STOP" || this.model.isServiceBreak(service)) {
                this.model.removeService(service.id);
            }
        }

        // Generate break services
        await this.generateBreaksFromShiftsAndSettings(settings, newShifts);

        // Generate new start/end services and breaks
        this.generateStartEndServicesFromSettings(settings, newShifts);

        // update services stop duration
        const oldStopDuration = this.model.getSettingsObjRef().stopDuration ?? this.model.getDefaultSettings().stopDuration;
        await this.updateServicesDuration(settings, this.model.getServicesObjRef(), oldStopDuration)
            .then(services => this.model.reloadServices(services));


        this.model.reloadSettings(settings);
        this.model.reloadShifts(newShifts);
        const [_persistedSettings, persistedShifts, persistedServices] = await Promise.all([this.persistSettingsModel(), this.persistShiftModel(), this.persistServiceModel()])

        this.eventChannel.fireEvent("onServicesReloaded", persistedServices);
        this.eventChannel.fireEvent("onShiftChange", ...persistedShifts);


        // Check if the stored schedule date is still valid, as the schedule start/finish times might have change
        const savedStartWeek = es.util.localStorage.get("ScheduleDate" + this.geoMapId);
        if (savedStartWeek && !this.util.isDateInsideScheduleTimeFrame(+savedStartWeek)) {
            es.util.localStorage.remove("ScheduleDate" + this.geoMapId);
        }
    }

    /**
     * High-level method that consolidates several actions that always take place when a schedule is generated. i.e.
     *  - Generate the schedule
     *  - Fetch the newly generated scheduleLayerViews
     *  - Fetch associated newly generated layerInfos
     *  - Reload the new services into the scheduleModel
     *  - reload the schedulePointMapping
     *
     * @returns {*}
     */
    generateScheduleAndUpdateModel() {
        const objective = es.util.localStorage.get("es_objective");

        // ensure that the latest service model is persisted in the database before requesting an optimisation
        let readyToGenerateDfd = this.persistServiceModel(true);
        return readyToGenerateDfd.then( () => {
            return es.api.schedule.generate(this.model.getId(), objective, {
                suppressDefaultErrorHandling: true,
                resolveWithXHR: true
            }).then(schedule => {
                es.log.log("es.api.schedule.generate : success", schedule);

                return es.api.layerView.getByGeoMapAndType(this.geoMapId, "ScheduleLayerView").then(scheduleLayerViews => {

                    const layerInfoRqs = [];
                    scheduleLayerViews.forEach(schedule => {
                        layerInfoRqs.push({layerViewId: schedule.id});
                    });

                    return Promise.all([
                        es.api.layerInfo.getAll(layerInfoRqs),
                        es.api.geoMap.get(this.geoMapId, {"noCache": true})
                    ]).then(([routeLayerInfos, geoMap]) => {

                        // Remove old layerViews "cached" on the client
                        this.featureViewModel.getLayersByType("ScheduleLayerView").forEach(lv => {
                            es.api.getEventChannel().fireEvent("onRemoveLayerView", lv.getId(), this.geoMapId, geoMap.lastModified);
                        });

                        scheduleLayerViews.forEach(lv => {
                            es.api.getEventChannel().fireEvent("onCreateLayerView", lv, routeLayerInfos.filter(layerInfo => layerInfo.layerViewId === lv.id)[0], geoMap.lastModified);
                        });

                        this.model.reloadServices(schedule.services);
                        this.model.reloadShifts(schedule.shifts);

                        this.baseMapModule.zoomToLayerViewExtents(scheduleLayerViews.map(lv => lv.id), 0, false);

                        this.eventChannel.fireEvent("onServicesReloaded", schedule.services);
                        this.eventChannel.fireEvent("onGenerateSchedule", 1);

                        this.updateRoutesColor(); //TODO Colors should be set on the server when creating layerViews
                        this.orderLayers();

                        return schedule;
                    });
                });

            }).fail(jqXHR => {

                if (jqXHR.responseText.startsWith("No solution found")) {
                    es.error.handleUIError("There is no route feasible with the current shift settings and stops requested.");
                } else if (jqXHR.responseText.startsWith("Some of your stops cannot be scheduled because they are too far from the road network") ||
                    jqXHR.responseText.startsWith("It was not possible to optimize and schedule all the locked stops")) {

                    let rs = jqXHR.responseText;
                    const esErrorCode = jqXHR.getResponseHeader("ES_ERROR_CODE");

                    rs = rs.substring(rs.lastIndexOf("[") + 1, rs.length - 1);

                    // TODO: fix esApp.map ref in esWebAPI
                    let problemFeatStr = `<br/><br/><select id="select_prob_feats" onchange='esApp.map.select.select(this.value.split("_")[0], this.value.split("_")[1])'>`;

                    const featKeys = rs.split(",");

                    featKeys.forEach((featKey, _idx) => {

                        const serviceId = featKey.trim();
                        if(!serviceId.startsWith("shift_")) {
                            const service = this.model.getService(serviceId);
                            if (!service) {
                                throw new Error("Could not find service with featKey " + featKey);
                            }
                            problemFeatStr += `<option value="${service.layerViewId}_${service.featureId}">${service.name?.replaceAll(es.util.label.ES_NEWLINE, " ")}</option>`;
                        }
                    });

                    problemFeatStr += "</select><br/><br/>Error code: " + esErrorCode;

                    let errMsg = jqXHR.responseText;
                    errMsg = errMsg.substring(0, errMsg.lastIndexOf("[") - 1) + problemFeatStr;

                    es.error.handleUIError(errMsg);
                    $("#select_prob_feats").trigger("change");
                } else {
                    if (jqXHR.status == 400) {
                        es.error.handleUIError(jqXHR.responseText);
                    } else {
                        es.error.handleServerErrorWithXHR(jqXHR);
                    }
                }
            })
        });
    };

    /**
     * High-level method that consolidates several actions that always take place when a mobile regenerates a shift on a schedule is
     *  - Generate the schedule
     *  - Fetch the newly generated scheduleLayerViews
     *  - Fetch associated newly generated layerInfos
     *  - Reload the new services into the scheduleModel
     *  - reload the schedulePointMapping
     *
     * @returns {*}
     */
    regenerateScheduleAndUpdateModel(shiftId) {
        const objective = es.util.localStorage.get("es_objective");
        return es.api.schedule.regenerate(this.model.getId(), shiftId, objective, {suppressDefaultErrorHandling: true, resolveWithXHR: true}).then(schedule => {
            es.log.log("es.api.schedule.regenerate : success", schedule);

            return es.api.layerView.getByGeoMapAndType(this.geoMapId, "ScheduleLayerView").then(scheduleLayerViews => {

                const layerInfoRqs = [];
                scheduleLayerViews.forEach(schedule => {
                    layerInfoRqs.push({layerViewId: schedule.id});
                });

                return Promise.all([
                    es.api.layerInfo.getAll(layerInfoRqs),
                    es.api.geoMap.get(this.geoMapId, {"noCache": true})
                ]).then(([routeLayerInfos, geoMap]) => {

                    // Remove old layerViews "cached" on the client
                    this.featureViewModel.getLayersByType("ScheduleLayerView").forEach(lv => {
                        es.api.getEventChannel().fireEvent("onRemoveLayerView", lv.getId(), this.geoMapId, geoMap.lastModified);
                    });

                    scheduleLayerViews.forEach(lv => {
                        es.api.getEventChannel().fireEvent("onCreateLayerView", lv, routeLayerInfos.filter(layerInfo => layerInfo.layerViewId === lv.id)[0], geoMap.lastModified);
                    });

                    this.model.reloadServices(schedule.services);
                    this.model.reloadShifts(schedule.shifts);

                    this.baseMapModule.zoomToLayerViewExtents(scheduleLayerViews.map(lv => lv.id), 0, false);

                    this.eventChannel.fireEvent("onServicesReloaded", schedule.services);
                    this.eventChannel.fireEvent("onGenerateSchedule", 1);

                    this.updateRoutesColor(); //TODO Colors should be set on the server when creating layerViews

                    return schedule;
                });
            });

        }).fail(jqXHR => {

            if( jqXHR.responseText.startsWith("Some of your stops cannot be scheduled because they are too far from the road network") ||
                jqXHR.responseText.startsWith("It was not possible to optimize and schedule all the locked stops") ){

                let rs = jqXHR.responseText;
                const esErrorCode = jqXHR.getResponseHeader("ES_ERROR_CODE");

                rs = rs.substring(rs.lastIndexOf("[") + 1, rs.length - 1);

                // TODO: fix esApp.map ref in esWebAPI
                let problemFeatStr = `<br/><br/><select id="select_prob_feats" onchange='esApp.map.select.select(this.value.split("_")[0], this.value.split("_")[1])'>`;

                const featKeys = rs.split(",");

                featKeys.forEach((featKey, _idx) => {
                    const service = this.model.getService(featKey);
                    if (!service) {
                        throw new Error("Could not find service with featKey " + featKey);
                    }
                    problemFeatStr += `<option value="${service.layerViewId}_${service.featureId}">${service.name?.replaceAll(es.util.label.ES_NEWLINE, " ")}</option>`;
                });

                problemFeatStr += "</select><br/><br/>Error code: " + esErrorCode;

                let errMsg = jqXHR.responseText;
                errMsg = errMsg.substring(0, errMsg.lastIndexOf("[") - 1) + problemFeatStr;

                es.error.handleUIError(errMsg);
                $("#select_prob_feats").trigger("change");
            } else {
                if(jqXHR.status == 400){
                    es.error.handleUIError(jqXHR.responseText);
                } else {
                    es.error.handleServerErrorWithXHR(jqXHR);
                }
            }
        });
    }

    /**
     * @param startAddress address to leave
     * @param finishAddress address to end
     * @param startTime time to leave the startAddress
     * @param finishTime time to be at the finishAddress
     * @param services to check feasibility
     * @returns {Promise<{[]}>} feasible services
     */
    checkFeasibility(startAddress, finishAddress, startTime, finishTime, services) {
        return es.api.schedule.checkFeasibility(this.model.getId(), startAddress, finishAddress, startTime, finishTime, services);
    }

    /**
     * Call to lock/unlock specific shifts to include/exclude them from future solutions.
     * @param shiftId
     * @param isLocked
     * @param isSuppressEvent
     */
    setShiftLock(shiftId, isLocked, isSuppressEvent = false): JQueryDeferred<boolean> {
        const shift = this.model.getShift(shiftId);
        if (!shift) {
            throw new Error(`No shift found with id: ${shiftId}`);
        }

        if (shift.locked === isLocked) {
            return es.util.deferred.resolved(false);
        }

        shift.locked = isLocked;

        this.model.editShift(shift);

        if (isSuppressEvent) {
            return es.util.deferred.resolved(true);
        }

        return this.persistShiftModel().then(() => {
            this.eventChannel.fireEvent("onShiftChange", shift);
            return es.util.deferred.resolved(true);
        });
    }

    /**
     * Call to lock/unlock specific service to include/exclude them from future solutions.
     * @param serviceId
     * @param isLocked
     * @param isSuppressEvent
     */
    setServiceLock(serviceId, isLocked, isSuppressEvent): JQueryDeferred<any> {
        const service = this.model.getService(serviceId);
        if (!service) {
            throw new Error(`No service found with id: ${serviceId}`);
        }

        if (service.locked === isLocked) {
            return es.util.deferred.resolved(false);
        }

        service.locked = isLocked;

        // If user locks a service then set override window and p1
        if (isLocked) {
            // If there is no stopInfo then overrideStartTime will already have been set by adding the service to the calendar.
            // Otherwise, reset the overrideStartTime to be the current stopInfo timeWindow arrive time
            if (service.stopInfo != undefined) {
                service.overrideStartTime = service.stopInfo.arrTime;
            }
        }

        this.model.editService(service);

        if (isSuppressEvent) {
            return es.util.deferred.resolved(true);
        }

        return this.persistServiceModel().then(() => {
            this.eventChannel.fireEvent("onEditService", service)
            return service;
        });
    }

    /**
     * Update the line color for the Overlay-graph render.
     * TODO Move this to esWebApp
     */
    updateRoutesColor() {
        this.featureViewModel.getLayersByType("ScheduleLayerView").forEach((lv) => {
            const active = this.model.isShiftActive(lv.getShiftId());
            const color = active ? this.model.getShiftColor(lv.getShiftId()) : 4291611852;

            let ss = lv.getStyleSettingsClone();
            ss.defaultStyle = es.util.style.types[ss.defaultStyle.$type].withColor(ss.defaultStyle, color);

            lv.setStyleSettings(ss);
        });
    }

    /**
     * 
     */
    orderLayers() {
        const lastIndex = this.featureViewModel.listLayers().length - 1;
        this.featureViewModel.getLayersByType("ScheduleLayerView").forEach((lv) => {
            const active = this.model.isShiftActive(lv.getShiftId());
            if (active) {
                this.featureViewModel.setIndex(lv.getId(), lastIndex);
            }
        });
    }

    /**
     * Persist the current services model to the server and silently reload the model in case of any changes (there shouldn't be though)
     * 
     * @returns {*}
     */
    persistServiceModel(): JQueryDeferred<any> {
        return es.api.schedule.updateServices(this.model.getId(), this.model.getServicesObjRef()).then(rs => {
            if (es.isDeveloper) {
                es.log.log("persistServiceModel", rs);
            }

            this.model.reloadServices(rs);
            return rs;
        });
    }

    /**
     * Persist the current services model to the server and silently reload the model in case of any changes (there shouldn't be though)
     * 
     * @returns {*}
     */
    persistShiftModel(): JQueryDeferred<any> {
        const allShifts = this.model.getShiftsClone();
        if (allShifts.length === 0) {
            // This is to avoid persisting the shift model when no settings have been confirmed.
            return es.util.deferred.resolved([]);
        }

        return es.api.schedule.updateShifts(this.model.getId(), allShifts).then(rs => {
            if (es.isDeveloper) {
                es.log.log("persistShiftModel", rs);
            }

            this.model.reloadShifts(rs, true);
            return rs;
        });
    }

    /**
     * Persist the current services model to the server and silently reload the model in case of any changes (there shouldn't be though)
     * 
     * @returns {*}
     */
    persistSettingsModel(): JQueryDeferred<any> {
        return es.api.schedule.updateSettings(this.model.getId(), this.model.getSettingsClone()).then(rs => {
            if (es.isDeveloper) {
                es.log.log("persistSettingsModel", rs);
            }

            this.model.reloadSettings(rs, true);
            return rs;
        });
    }

    /**
     * "Unschedule" or move a scheduled service to shortlist
     * 
     * @param serviceId
     * @param suppressRemoveScheduleLayerView
     */
    unScheduleService(serviceId, suppressRemoveScheduleLayerView = false): JQueryDeferred<void> {
        const service = this.getServiceClone(serviceId);

        let shiftId;
        // unlock shift
        if (service?.stopInfo?.shiftId){
            shiftId = service.stopInfo.shiftId;
            this.setShiftLock(service.stopInfo.shiftId, false);
        } else {
            const shift = this.getShiftByObjRef(service.overrideStartTime * 1000);
            if(shift) {
                shiftId = shift.shiftId;
                this.setShiftLock(shiftId, false);
            }
        }

        // remove route lv
        if (!suppressRemoveScheduleLayerView) {
            this.removeScheduleLayerViews(service.id);
        }

        // edit service
        service.stopInfo = null;
        service.overrideStartTime = null;
        service.locked = false;

        this.model.editService(service);
        this.recalculateStopNumbers(shiftId);

        return this.persistServiceModel().then(() => {
            this.eventChannel.fireEvent("onEditService", service)
        });
    }

    /**
     * Move a service from one timeslot to another
     * 
     * @param serviceId
     * @param start
     * @param duration
     * @param locked
     * @param removeScheduleLayerView
     */
    async moveService(serviceId, start, duration, locked, removeScheduleLayerView = true) {
        const service = this.getServiceClone(serviceId);

        const currentShift = this.util.getShiftFromService(service);
        const targetShift = this.util.getShiftFromDate(start);

        if (currentShift) {
            this.setShiftLock(currentShift.shiftId, false, true);
        }
        if (targetShift && removeScheduleLayerView) {
            this.setShiftLock(targetShift.shiftId, false, true);
        }

        delete service.stopInfo;
        service.overrideStartTime = start;
        service.locked = locked;
        service.duration = duration ? duration : service.duration;

        if (removeScheduleLayerView) {
            const shiftIdsToRemove = [];
            if(currentShift) {
                shiftIdsToRemove.push(currentShift.shiftId);
            }
            if(targetShift) {
                shiftIdsToRemove.push(targetShift.shiftId)
            }
            await this.removeScheduleLayerViewsByShiftId(shiftIdsToRemove);
        }

        this.model.editService(service);
        targetShift && this.recalculateStopNumbers(targetShift.shiftId);
        currentShift && this.recalculateStopNumbers(currentShift.shiftId);
        return $.when(
            this.persistServiceModel(),
            this.persistShiftModel()
        ).then(() => {
            this.eventChannel.fireEvent("onEditService", service)
            return service;
        });
    }

    /**
     * Changes the duration of a service.
     * @param serviceId
     * @param duration
     */
    changeServiceDuration(serviceId, duration?: number): JQueryDeferred<any> {
        const service = this.getServiceClone(serviceId);

        if(!service) {
            throw Error("Service " + serviceId + "not found");
        }

        const currentShift = this.util.getShiftFromService(service);

        if (currentShift) {
            this.setShiftLock(currentShift.shiftId, false);
        }

        service.stopInfo = null;
        service.duration = duration ? duration : service.duration;

        this.removeScheduleLayerViews(serviceId);
        this.model.editService(service);
        return this.persistServiceModel().then(() => {
            this.eventChannel.fireEvent("onEditService", service)
            return service;
        });
    }

    /**
     * Add list of services
     * 
     * @param services array of services to add.
     * @param isReturnPersistedModel optional default false. option to return the persisted model here rather than
     *                               just allowing the auto-persist action happen at some unknown point in the future
     *                               (which makes writing functional tests hard).
     * @return {*|null|*[]}
     */
    addServices(services, isReturnPersistedModel: boolean): any[]|undefined {
        if (!services || services.length === 0) {
            return;
        }

        const maxServicesToAdd = this.model.getNumRemainingServices();
        let numServicesToBeAdded = services.filter(s => !this.util.isFeatureSchedule(s.layerViewId, s.featureId)).length;
        if(services.length === MAX_SELECT_LIMIT - this.model.getShiftsObjRef().length){
            // TOR9-19906 special case
            numServicesToBeAdded = numServicesToBeAdded + this.model.getShiftsObjRef().length;
        }

        if(numServicesToBeAdded > maxServicesToAdd) {
            const scheduleDays = this.model.getShiftsObjRef().length > 0 ? this.model.getShiftsObjRef().length : Math.min(this.model.maxRoutingDaysPerSchedule, 5);
            let msg = $i18n('_Unscheduled_Max_Services', [
                this.model.getServicesLimit(),
                scheduleDays,
                numServicesToBeAdded + (numServicesToBeAdded === MAX_SELECT_LIMIT ? " (which is the selection limit)" : ""),
                this.model.getStopServicesObjRef().length
            ]);
            es.alert.alertOk(msg);
            return null;
        }

        let addedServices = [];
        for(const s of services) {
            const createdService = this.model.addService(s.layerViewId, s.featureId, s.name, s.duration, s.address, s.overrideStartTime, s.stopInfo);

            if(createdService !== null) {
                addedServices.push(createdService);
            }
        }
        
        const persistModelDfd = this.persistServiceModel();

        this.eventChannel.fireEvent("onAddService", addedServices);
        this.model.getEventChannel().fireEvent("onSchedulePointMappingUpdate");

        if(isReturnPersistedModel === true){
            return persistModelDfd.then(allServices => {
                es.log.log("addServices : persist done", addedServices, allServices);
                return addedServices; // return addedServices instead of
            });
        }

        return addedServices;
    }

    /**
     * 
     * @param geom 
     * 
     * @returns 
     */
    addServicesFromGeom(geom): JQueryDeferred<void> {
        let selectedFeatures = [];
        let geometrySelectionTimeout;

        return es.api.geoShape.store({
            geom
        }).then((geoShape) => {
            let criteria = {
                $type: "Interacts",
                'geoShapeKey': geoShape.key
            };

            return this.util.getFeaturesRequest(criteria);
        }).then((request) => {
            geometrySelectionTimeout = setTimeout(() => {
                $().loadingOverlay('start');
            }, 1000); // If server is slow and it takes more than one second to process show a loading message

            return es.api.feature.render(request);
        }).then((rs) => {
            let layers = rs.layers;
            for (let i = layers.length; i-- > 0;) {
                let layerModel = this.layerModule.getById(layers[i].layerViewId);
                let isResOK = (this.baseMapModule.getResolution() >= layerModel.getMinResolution()) && ((layerModel.getMaxResolution() <= 0) || (this.baseMapModule.getResolution() <= layerModel.getMaxResolution()));

                if (layerModel.isSelectable() && isResOK) {
                    let layerFeatures = layers[i].features;
                    for (var f = layerFeatures.length; f-- > 0; ) {
                        let feature = layerFeatures[f];
                        selectedFeatures.push(feature);
                    }
                }
            }

            es.api.getEventChannel().fireEvent("onMultiSelectFeatures", selectedFeatures);
        }).always(function () {
            if (geometrySelectionTimeout) { // remove timeout
                clearTimeout(geometrySelectionTimeout);
            }

            $().loadingOverlay('stop'); // remove overlay
        });
    }

    /**
     * Add a service to the schedule.
     *
     * @param layerViewId associated feat lvId
     * @param featureId associated feat id
     * @param name service title, also used as label
     * @param duration Specifies the duration of the service in seconds, i.e. how long it takes at the customer site.
     * @param address (optional) gh address obj
     * @param overrideStartTime (optional)
     * @param stopInfo (optional) pre-defined stop info for start / end services
     * @param locked (optional) if the stop is locked
     * 
     * @returns {{duration: (*|number), name, id: string}|null}
     */
    addService(layerViewId: number, featureId: number, name: string, duration: number, address = null, overrideStartTime = null, stopInfo = null, locked = false) {
        const service = this.model.addService(layerViewId, featureId, name, duration, address, overrideStartTime, stopInfo, locked);

        if (service === null) {
            return null;
        }

        this.persistServiceModel().then(() => {
            this.eventChannel.fireEvent("onAddService", service);
            this.model.getEventChannel().fireEvent("onSchedulePointMappingUpdate");
        });

        return service;
    }

    /**
     * 
     * @param id 
     * @param isSuppressEvent 
     */
    removeService(id, isSuppressEvent = false) {
        this.removeScheduleLayerViews(id);
        const serviceShift = this.util.getShiftFromService(this.getServiceClone(id));
        this.model.removeService(id);
        serviceShift && this.recalculateStopNumbers(serviceShift.shiftId);

        this.persistServiceModel().then(() => {
            if (isSuppressEvent !== true) {
                this.eventChannel.fireEvent("onRemoveService", id);
            }
        });
    }

    /**
     * 
     * @param services 
     */
    removeServices(services) {
        services.forEach( s => {
            this.removeScheduleLayerViews(s.id);
            const serviceShift = this.util.getShiftFromService(this.getServiceClone(s.id));
            this.model.removeService(s.id);
            serviceShift && this.recalculateStopNumbers(serviceShift.shiftId);
        });

        this.persistServiceModel().then(() => {
            this.eventChannel.fireEvent("onRemoveService", -1);
        });
    }

    /**
     * 
     */
    removeAllServices() {
        this.model.removeAllServices();
        this.removeScheduleLayerViews();

        this.persistServiceModel().then(() => {
            this.eventChannel.fireEvent("onRemoveService", -1);
        });
    }

    /**
     * 
     */
    removeUnScheduledServices() {
        this.model.removeUnScheduledServices();

        this.persistServiceModel().then(() => {
            this.eventChannel.fireEvent("onRemoveService", -1);
        });
    }

    /**
     * 
     * @param moveLocked 
     * 
     * @returns 
     */
    moveServicesToShortlist(moveLocked = false): JQueryDeferred<void> {
        const affectedShiftIds = new Set();
        const shifts = this.model.getShiftsObjRef();
        const hasUserConfirmedSettings = this.model.getShiftsObjRef().length !== 0;

        const moveServiceLogic = service => {
            if((service.locked && !moveLocked) || this.isServiceAnyStartOrEnd(service)) {
                return false;
            }
            delete service.stopInfo;
            delete service.locked;
            delete service.overrideStartTime;

            //Reset the duration if it changed
            const duration = this.model.isServiceBreak(service) ?
            this.model.getSettingsObjRef().breakDuration : this.model.getSettingsObjRef().stopDuration;
            if (service.duration !== duration) {
                service.duration = duration;
            }

            this.model.editService(service);
            return true;
        };

        // If the user hasn't confirmed the settings yet, the shifts array is empty, so we have to iterate over all services.
        // If there are shifts, we need to perform additional logic to check if the shift is locked, and
        if (!hasUserConfirmedSettings) {
            this.model.getServicesObjRef().forEach(s => moveServiceLogic(s));
        } else {
            for(const shift of shifts) {
                if(shift.locked && !moveLocked) {
                    continue;
                }

                const shiftServices = this.util.getServicesByShiftClone(shift.shiftId);
                shiftServices.forEach(s => {
                    const serviceEdited = moveServiceLogic(s);
                    if (serviceEdited) {
                        affectedShiftIds.add(shift.shiftId);
                    }
                });

                // set the shift as unlocked if we are clearing the locked services
                // otherwise, the next time the user tries to create a schedule all shifts are locked and creates an odd behaviour
                if (moveLocked && shift.locked) {
                    shift.locked = false;
                    this.model.editShift(shift);
                }
            }
        }

        return this.removeScheduleLayerViewsByShiftId([...affectedShiftIds.values()]).then(() => {
            return Promise.all([
                this.persistShiftModel(),
                this.persistServiceModel()
            ]).then(() => {
                this.eventChannel.fireEvent("onServicesReloaded", this.model.getServicesObjRef());
                this.eventChannel.fireEvent("onClearSchedule");
            });
        });
    }

    /**
     * THIS METHOD IS TO BE USED BEFORE SAVING THE ROUTE DEFAULTS OR MANUALLY SETTING A START/END LOCATION VIA THE CALENDAR.
     * IT IS NOT INTENDED TO BE USED ELSEWHERE.
     * This method checks if the route startAddress and endAddress are *linked* with features already on the map
     * This is validated using the *locationId* property in each of the Address
     * If the user chooses on of the addresses to be a *Saved Location* or a *Azure address*, prior to saving the settings
     * we need to create those locations in the Address Points layer
     * @returns {jQuery} Deferred with the updated routeSettings
     * @param routeSettings
     */
    checkOrCreateAddressPoints(routeSettings): JQueryDeferred<any> {
        let addressesToCreate = [];

        if (routeSettings.startAddress !== undefined && routeSettings.startAddress !== null && routeSettings.startAddress.locationId === undefined) {
            const addressToCreate = {...routeSettings.startAddress, prop: "startAddress"}
            addressesToCreate.push(addressToCreate);
        }
        if (routeSettings.endAddress !== undefined && routeSettings.endAddress !== null && routeSettings.endAddress.locationId === undefined) {
            const addressToCreate = {...routeSettings.endAddress, prop: "endAddress"}
            addressesToCreate.push(addressToCreate);
        }

        if (addressesToCreate.length === 0) {
            return es.util.deferred.resolved(routeSettings);
        }

        let points = addressesToCreate.map(addr => ({
            name: addr.name,
            address: addr.streetHint,
            lonLat: [addr.lon, addr.lat],
            point: [addr.lon, addr.lat]
        }));


        const matchAndAssignFeatureId = (addressObj, featureId) => {
            addressObj["locationId"] = `${this.addressPointLayerViewId}_${featureId}`;
            const routeSettingsPropName = addressObj.prop;
            delete addressObj.prop;
            routeSettings[routeSettingsPropName] = addressObj;
        }

        return this.util.getAddressPointsOnCoordinates(points).then(({matchedFeatures, unmatchedPoints}) => {
            addressesToCreate.forEach((address, idx) => {
                const matchedFeature = matchedFeatures.get(idx);
                if (matchedFeature) {
                    matchAndAssignFeatureId(address, matchedFeature.id);
                }
            });

            if(unmatchedPoints.length === 0) {
                return routeSettings;
            }

            // Both addresses can be the same point. We should create only one feature in the Address Points layer
            points = _.uniqWith(points, (arrVal, othVal) => _.isEqual(arrVal.point, othVal.point));
            points = points.map(point => {
                delete point.lonLat;
                return point;
            });

            return es.util.mapScope.createAddressPoints(this.addressPointLayerViewId, points, this.editModule).then((createResult) => {
                for (let [index, address] of addressesToCreate.entries()) {
                    if (matchedFeatures.get(index)) {
                        continue;
                    }

                    const rs = _.find(createResult, (rs) => rs.action.attrs.NAME === address.name && address.streetHint == rs.action.attrs.ADDRESS);
                    if (!rs) {
                        return es.util.deferred.rejected("Could not find a point feat created for " + address.name);
                    }

                    matchAndAssignFeatureId(address, rs.featureId);
                }

                return routeSettings;
            });
        });
    }

    /**
     * 
     * @param feature 
     * 
     * @returns 
     */
    getDurationFromFeature(feature): number {
        const settings = this.model.getSettingsObjRef();
        let duration = settings.stopDuration;
        const stopTimeAttribute = settings.stopTimeFieldMapping[this.featureViewModel.getLayerById(feature.layerViewId).getLayerMetaId()];

        if (stopTimeAttribute && feature.attrs?.[stopTimeAttribute] != null) {
            duration = feature.attrs[stopTimeAttribute] * 60;
        }

        return duration;
    }

    /**
     * 
     * @param layerViewIds 
     * @param numServicesToAdd 
     * @param skip 
     * 
     * @returns 
     */
    addServicesByRecommendationIndex(layerViewIds: number[], numServicesToAdd: number, skip=0): JQueryDeferred<any>|undefined {
        // Interlace adding points from each dataset, so the % of points added is similar between datasets
        const mergeArrays = (pointsPerLayer => {
            let finalPoints = _.flatten(_.zip(...pointsPerLayer));
            finalPoints = finalPoints.filter(p => p !== undefined);
            finalPoints = _.orderBy(finalPoints, (feat) => feat.attrs.ES_RECOMMENDATION, ["desc"]);
            return finalPoints;
        });

        const mapFeatureToService = (feat) => {
            return {
                layerViewId: feat.layerViewId,
                featureId: feat.id,
                name: feat.label,
                duration: this.getDurationFromFeature(feat),
                address: {
                    'lon': feat.geom[1],
                    'lat': feat.geom[2],
                    'locationId': `${feat.layerViewId}_${feat.id}`
                }
            };
        }

        // return a new list with all the points that aren't already on the schedule model;
        const discardAlreadyScheduledServices = (newServices) => {
            const currentServices = this.getServicesObjRef();
            return _.differenceWith(newServices, currentServices, (val, otherVal) => val.layerViewId === otherVal.layerViewId && val.featureId === otherVal.featureId);
        }

        // Request a buffered amount of services to add, because some points could already be in the unscheduled list
        const numPointsToRequest = Math.ceil(_.clamp(numServicesToAdd * 10 / layerViewIds.length, 10, 100));

        const addFeaturesAsServices = (features) => {
            let services = features.map(mapFeatureToService);
            services = discardAlreadyScheduledServices(services).slice(0, numServicesToAdd);

            if(services.length < numServicesToAdd && features.length === numPointsToRequest) {
                // we don't have enough new services to add. Call this method again with the new values to add
                // update the args to pass the remaining amount of services to add, and skip the number of points already requested
                if(services.length !== 0) {
                    services = this.addServices(services);
                }
                return this.addServicesByRecommendationIndex(layerViewIds, numServicesToAdd - services.length, skip + features.length).then(servs => {
                    return [...services, ...servs];
                });
            }
            return this.addServices(services);
        }

        const sortOrder = {"attr": "ES_RECOMMENDATION", "descending": true};
        if(_.isArray(layerViewIds)) {
            const dfds = [];

            layerViewIds.forEach(layerViewId => {
                dfds.push(es.api.feature.list(layerViewId, undefined, sortOrder, skip, numPointsToRequest, undefined, undefined));
            });

            return $.when.apply($, dfds).then((...pointsPerLayer) => {
                const features = mergeArrays(pointsPerLayer);
                return addFeaturesAsServices(features);
            });
        }
    }

    /**
     * 
     * @param layerViewId 
     * 
     * @returns 
     */
    getActiveRecommendation(layerViewId: number) {
        const layerMetaId = this.featureViewModel.getLayerById(layerViewId).getLayerMetaId();

        return this.model.getActiveRecommendation(layerMetaId);
    }

    /**
     * 
     * @param layerViewId 
     * @param recommendationId 
     * 
     * @returns 
     */
    setActiveRecommendation(layerViewId: number, recommendationId: number): JQueryDeferred<void> {
        return es.api.schedule.setActiveRecommendation(layerViewId, recommendationId).then(() => {
            const layerMetaId = this.featureViewModel.getLayerById(layerViewId).getLayerMetaId();
            this.model.setActiveRecommendation(layerMetaId, recommendationId);

            return es.util.deferred.resolved();
        });
    }

    /**
     * 
     * @param layerView 
     * @param layerInfo 
     * 
     * @returns 
     */
    setActiveRecommendationForLayer(layerView: ILayerView, layerInfo: LayerInfo) {
        if (layerView.geomType !== "POINT") {
            return;
        }

        // check if there are recommendations for the dataset just added.
        // If there are, set the most recent one as the active
        es.api.schedule.getRecommendationsForGeoMap(this.geoMapId).then(recommendations => {
            const recommendationForLayer = _.find(_.sortBy(recommendations, "lastModified"), (recommendation) => recommendation.layerMetaId === layerInfo.layerMetaId);

            if (recommendationForLayer) {
                this.setActiveRecommendation(layerView.id, recommendationForLayer.id);
            }
        });
    }

    /**
     * 
     * @param layerMetaId 
     */
    removeActiveRecommendationForLayer(layerMetaId: number) {
        this.model.setActiveRecommendation(layerMetaId, undefined);
    };

    /**
     * Fetch full features referenced by given services
     * @param services
     * @returns {*}
     */
    getFeatsForServices(services) {
        const featKeys = [];

        services.forEach(s => {
            const featKey = featKeys.find(obj => obj.lvId == s.layerViewId);
            if (featKey) {
                featKey.featIds.push(s.featureId);
            } else {
                featKeys.push( {"lvId": s.layerViewId, "featIds": [s.featureId]} );
            }
        });

        return es.api.feature.getByFeatKeys(featKeys);
    }

    /**
     * Fetch features for settings start and end address, if any.
     * @return {*}
     */
    getFeatsForSettingsStartEnd() {
        const settings = this.model.getSettingsClone();
        const featKeys = [];

        if (settings?.startAddress?.locationId) {
            featKeys.push(settings.startAddress.locationId);
        }
        if (settings?.endAddress?.locationId) {
            featKeys.push(settings.endAddress.locationId);
        }

        return es.api.feature.getByFeatKeys(featKeys);
    }

    /**
     * Calculate the driveTime and distance for the given service. 0 values will be returned if not applicable (e.g. when drive time info hasn't been calculated yet).
     * @param serviceId
     * @return {{distance, drivingTime: (number|*)}|{distance: number, drivingTime: number}}
     */
    getDriveTimeAndDistance(serviceId): {distance:number, drivingTime: number} {
        const service = this.model.getService(serviceId);
        if (this.model.isServiceScheduledInSolution(service)) {
            if (service.stopInfo.stopNo <= 1) { // if there's a depot start/end address it'll be stopNo 0
                return {
                    distance: service.stopInfo.distance,
                    drivingTime: service.stopInfo.drivingTime
                };
            }
            
            const previousStop = this.model.getServicesObjRef().find(s =>
                this.model.isServiceScheduledInSolution(s) &&
                s.stopInfo?.shiftId === service.stopInfo?.shiftId &&
                s.stopInfo?.stopNo === service.stopInfo?.stopNo - 1
            );

            if (previousStop) {
                return {
                    distance: service.stopInfo.distance - previousStop.stopInfo.distance,
                    drivingTime: service.stopInfo.drivingTime - previousStop.stopInfo.drivingTime
                };
            }
        }

        return {distance: 0, drivingTime: 0};
    }

    /**
     * Perform a check on services in the model versus associated features to see have they become out of sync.
     *
     * Will handle and/or silently fix issues like out of sync:
     *    - Moved locations in shifts
     *    - Moved locations in settings
     *    - Changed labels
     *    - Moved service locations
     *    - Deleted feats
     *
     * @return a summary obj of issues.
     */
    syncScheduleWithFeatures(): JQueryDeferred<any> {
        return this.getFeatsForServices(
            this.model.getServicesObjRef().filter(s => !this.model.isServiceBreak(s))
        ).then(feats => {
            const rs = {
                missingServices: [],
                quietlyRemovedServices: [],
                scheduledIds: [],
                shortlistedIds: [],
                mismatchedLabelIds: [],
                isAllMissing: false,
                startOrEndSettingsChanged: false,
                durationChangedIds: []
            };

            /**
             * Detect if the feat referenced by given serviceId is already referenced by another serviceId in given list.
             * @param sId
             * @param list
             * @return {boolean|*}
             */
            const isServicePointAlreadyInList = (sId, list) => {
                const service = this.model.getServicesObjRef().find(s => s.id === sId);
                if (service) {
                    return list.findIndex(id => {
                        return id === this.model.getServicesObjRef().find(s => s.layerViewId === service.layerViewId && s.featureId === service.featureId).id;
                    }) > -1;
                }

                return false;
            };

            this.model.getServicesObjRef().filter(s => !this.model.isServiceBreak(s)).forEach(s => {
                const settings = this.model.getSettingsObjRef();
                const feat = feats.find(f => f.layerViewId === s.layerViewId && f.id === s.featureId);

                //es.log.log("processing", s, feat);
                if (!feat) {
                    if (this.model.isServiceScheduledInSolution(s) || this.model.isServiceAnyStartOrEnd(s)) {
                        // These services will be notified to the user
                        rs.missingServices.push(es.util.object.deepCopy(s));
                    } else {
                        // These services where unscheduled. Quietly remove them.
                        rs.quietlyRemovedServices.push(es.util.object.deepCopy(s));
                    }

                    if (this.model.isServiceAnyStartOrEnd(s)) {
                        const shift = this.util.getShiftFromService(s);
                        shift.locked = false;

                        if (this.model.isServiceStartOnly(s)) {
                            delete settings.startAddress;
                            delete shift.startAddress;
                        } else if (this.model.isServiceEndOnly(s)) {
                            delete settings.endAddress;
                            delete shift.endAddress;
                        } else if (this.model.isServiceStartEndOnly(s)) {
                            delete settings.startAddress;
                            delete settings.endAddress;
                            delete shift.startAddress;
                            delete shift.endAddress;
                        }

                        settings.roundTrip = false;
                        rs.startOrEndSettingsChanged = true;
                    }

                    this.removeScheduleLayerViews(s.id);

                    const shift = this.util.getShiftFromService(s);
                    if (shift) {
                        this.setShiftLock(shift.shiftId, false, true);
                    }

                    this.model.removeService(s.id);
                } else {
                    const f = feat;
                    const locationId = f.layerViewId + "_" + f.id;

                    // Try to update out of sync labels
                    let label = null;
                    if( es.util.map.isMapScopeLayerView(this.layerModule.getById(f.layerViewId).getType()) && s.name === f.attrs['NAME']){
                        // skip - services from savedLocations could have a user-entered name like 'Home' so will always be different. not ideal way to detect this. TOR9-16988
                    } else if (s.name !== f.label) {
                        rs.mismatchedLabelIds.push(s.id);
                        es.log.log("mismatchLabel", f, s);
                        label = f.label;
                        s.name = f.label ?? "";
                        this.model.editService(s, true);
                    }

                    if (f.hasOwnProperty("geom") && f.geom.length === 3) {
                        const lon = f.geom[1], lat = f.geom[2];

                        if (s.address.lon !== lon || s.address.lat !== lat) {
                            const updateAddr = (addr: object, label?: object) => {
                                addr.lon = lon;
                                addr.lat = lat;
                                if (label) {
                                    addr.name = label;
                                    addr.streetHint = label;
                                }
                            }

                            const updateSettingsAddresses = () => {
                                if (this.model.isServiceStartOnly(s)) {
                                    updateAddr(settings.startAddress, label);
                                } else if (this.model.isServiceEndOnly(s)) {
                                    updateAddr(settings.endAddress, label);
                                } else if (this.model.isServiceStartOrEndOnly(s)) {
                                    updateAddr(settings.startAddress, label);
                                    updateAddr(settings.endAddress, label);
                                }
                                rs.startOrEndSettingsChanged = true;
                            }

                            const updateShiftAddr = (shift) => {
                                if(shift?.startAddress?.locationId === locationId){
                                    updateAddr(shift.startAddress, label);
                                }
                                if(shift?.endAddress?.locationId === locationId){
                                    updateAddr(shift.endAddress, label);
                                }
                                rs.startOrEndSettingsChanged = true;
                            }

                            const updateShiftsAndSettings = () => {
                                if (this.model.isServiceAnyStartOrEnd(s)) {
                                    const shift = this.util.getShiftFromService(s);
                                    shift.locked = false;
                                    updateShiftAddr(shift);
                                    updateSettingsAddresses();
                                }
                            }

                            if (this.model.isServiceScheduledInSolution(s) || (this.model.isServiceAnyStartOrEnd(s) && this.model.hasSolution())) {
                                updateShiftsAndSettings();
                                this.removeScheduleLayerViews(s.id);

                                updateAddr(s.address);

                                const shift = this.util.getShiftFromService(s);
                                if(shift){
                                    this.setShiftLock(shift.shiftId, false);
                                }

                                if (!this.model.isServiceAnyStartOrEnd(s)) {
                                    s.stopInfo = null;
                                }

                                this.model.editService(s);

                                if (!isServicePointAlreadyInList(s.id, rs.scheduledIds)) {
                                    rs.scheduledIds.push(s.id);
                                }
                                es.log.log("auto-updating scheduled service location from", s.address.lon, s.address.lat, "to", lon, lat, s);

                            } else if (!this.model.isServiceScheduledInSolution(s) || this.model.isServiceAnyStartOrEnd(s)) {
                                updateShiftsAndSettings();

                                updateAddr(s.address);

                                this.model.editService(s);

                                if(!isServicePointAlreadyInList(s.id, rs.shortlistedIds)) {
                                    rs.shortlistedIds.push(s.id);
                                }

                                es.log.log("auto-updating shortlisted service location ", s.address, "to", lon, lat);
                            } else {
                                console.error("Unexpected service state", s);
                            }
                        }

                        if (!this.model.isServiceScheduledInSolution(s)) {
                            const layerMetaId = this.layerModule.getById(f.layerViewId).getLayerMetaId();
                            const timeAttrName = this.getSettingsObjRef()['stopTimeFieldMapping'][layerMetaId];

                            if(timeAttrName) {
                                const val = f.attrs[timeAttrName];
                                const expectedVal = val * 60;
                                if (val && val > 0 && s.duration !== expectedVal) {
                                    es.log.log("auto-updating shortlisted service duration ", s.duration, "to", expectedVal, s);
                                    
                                    s.duration = expectedVal;
                                    this.model.editService(s);
                                    rs.durationChangedIds.push(s.id);
                                }
                            }
                        }
                    }
                }
            });

            rs.isAllMissing = rs.missingServices.length > 0 && this.model.getServicesObjRef().length === this.model.getShiftsObjRef().length;
            es.log.info("syncScheduleWithFeatures", rs);

            if (rs.isAllMissing || rs.startOrEndSettingsChanged) {
                this.removeScheduleLayerViews();
            } else if (!this.model.hasSolution() && this.featureViewModel.getLayersByType("ScheduleLayerView").length > 0) {
                es.log.warn("Invalid state detected: hasSolution but no ScheduleLayerView");
                this.removeScheduleLayerViews();
            }

            rs.scheduledIds = _.uniq(rs.scheduledIds);

            if( rs.shortlistedIds.length > 0 ||
                rs.scheduledIds.length > 0 ||
                rs.missingServices.length > 0 ||
                rs.mismatchedLabelIds.length > 0 ||
                rs.startOrEndSettingsChanged ||
                rs.quietlyRemovedServices.length > 0 ||
                rs.durationChangedIds.length > 0) {

                return Promise.all([
                    this.persistServiceModel(),
                    this.persistShiftModel(),
                    rs.startOrEndSettingsChanged ? this.persistSettingsModel() : es.util.deferred.resolved()
                ]).then(() => {
                    this.eventChannel.fireEvent("onServicesReloaded", this.model.getServicesObjRef());
                    return rs;
                });
            } else {
                return es.util.deferred.resolved(rs);
            }
        });
    }

    /**
     * 
     * @param schedule 
     */
    public reload(schedule: Schedule) {
        this.model.reload(schedule);
    }

    /**
     * Recalculate the stop numbers for a particular shift
     * @param shiftId
     */
    recalculateStopNumbers(shiftId: string) {
        const services = this.util.getServicesByShiftClone(shiftId);
        let stopNo = 0;
        for (const service of services) {
            if (!this.model.isUserService(service)) {
                continue;
            }

            stopNo++;
            if (service.stopInfo) {
                service.stopInfo.stopNo = stopNo;
                this.model.editService(service);
            }
        }
    }

    /**
     * 
     * @param costPerDistance 
     * @returns 
     */
    updateCostPerDistance(costPerDistance): JQueryDeferred<any> {
        this.model.setCostPerDistance(costPerDistance);
        return this.persistSettingsModel();
    }

    /**
     * 
     * @param newStartTimeInSeconds 
     * 
     * @returns 
     */
    shiftSchedule(newStartTimeInSeconds: number): JQueryDeferred {
        const settings = this.getSettingsObjRef();
        const secondsInDay = 24*60*60;
        // Update settings with the new startDate and finishDate
        const newSettings = {...settings};
        newSettings.startDate = newStartTimeInSeconds;

        // Create the new services array with only the ones on the shortlist
        // Scheduled services will be appended to this list in the for loop below
        const newServices = this.getServicesObjRef().filter(s => !s.stopInfo && !s.overrideStartTime);
        const sortedShifts = this.getShiftsObjRef().sort((a, b) => a.earliestStart - b.earliestStart);
        const newShifts = [];
        let previousShiftStartDateInSeconds = newStartTimeInSeconds + settings.shiftStartTime - secondsInDay;
        for (const shift of sortedShifts) {
            let targetDayInSeconds = previousShiftStartDateInSeconds + secondsInDay;
            let isTargetDayAvailable = this.util.isDayAvailable(targetDayInSeconds * 1000);

            // This block of code skips days if they are unavailable due to the settings day availability
            // If the target day is unavailable, add another day and test is again and again until the
            // target days falls in an available day of the week
            while (!isTargetDayAvailable) {
                targetDayInSeconds = targetDayInSeconds + secondsInDay;
                isTargetDayAvailable = this.util.isDayAvailable(targetDayInSeconds * 1000);
            }

            const shiftAmount = targetDayInSeconds - shift.earliestStart;
            const newShift = {...shift};
            newShift.earliestStart = shift.earliestStart + shiftAmount;
            newShift.latestEnd = shift.latestEnd + shiftAmount
            newShifts.push(newShift);

            previousShiftStartDateInSeconds = newShift.earliestStart;

            // Since we are updating these services and iterating again in the loop
            // we have to deep copy it because the getServicesByDateObjRef would retrieve the updated services as well
            const servicesByDate = es.util.object.deepCopy(this.util.getServicesByDateObjRef(shift.earliestStart));

            for (const service of servicesByDate) {
                if (service.stopInfo?.arrTime)  {
                    service.stopInfo.arrTime = service.stopInfo.arrTime + shiftAmount;
                    service.stopInfo.endTime = service.stopInfo.endTime + shiftAmount;
                    service.stopInfo.shiftId = shift.shiftId;
                }

                if (service.overrideStartTime) {
                    service.overrideStartTime = service.overrideStartTime + shiftAmount;
                }

                newServices.push(service);
            }

        }

        // The finish day isn't necesseraly just adding the amount of shifted days to the current finish date
        // because of the day availability, it can shifted more times
        // we will infer the date from the last shift to get a more accurate result.
        newSettings.finishDate = dayjs.unix(newShifts[newShifts.length - 1].earliestStart).startOf("day").unix();

        this.model.reloadServices(newServices);
        this.model.reloadShifts(newShifts);
        this.model.reloadSettings(newSettings);
        this.updateRoutesColor();

        return $.when(
            this.persistServiceModel(),
            this.persistSettingsModel(),
            this.persistShiftModel()
        );
    }
}

class ScheduleModuleUtil {
    private schedule: ScheduleModule;
    private model: ScheduleModel;
    private baseMapModule: AzureBaseMap;
    private layerModule: Layer;

    constructor(schedule: ScheduleModule, model: ScheduleModel, baseMapModule: AzureBaseMap, layerModule: Layer) {
        this.schedule = schedule;
        this.model = model;
        this.baseMapModule = baseMapModule;
        this.layerModule = layerModule;
    }

    /**
     * Convenience method to generate a basic service from a feat (that must have a location obviously)
     * @param feat
     */
    createServiceFromFeat(feat) {
        return this.schedule.addService(
            feat.layerViewId,
            feat.id,
            feat.label,
            this.schedule.getDurationFromFeature(feat),
            this.getAddressFromFeat(feat)
        );
    }

    /**
     * 
     * @param feat 
     */
    removeServicesFromFeat(feat) {
        const services = this.model.getLocationMappingObjRef().get(feat.layerViewId, feat.id);
        if (services) {
            this.schedule.removeServices(services);
        }
    }

    /**
     * 
     * @param feat 
     * 
     * @returns 
     */
    getServicesFromFeat(feat) {
        return this.model.getLocationMappingObjRef().get(feat.layerViewId, feat.id);
    }

    /**
     * Get shift for a given unix timestamp date
     * @param date unix
     */
    getShiftFromDate(date: number) {
        return this.model.shifts.find(s => dayjs.unix(s.earliestStart).startOf("day").unix() <= date && s.latestEnd >= date);
    }

    /**
     * 
     * @param shiftId 
     * 
     * @returns 
     */
    getShift(shiftId: string) {
        return this.model.getShift(shiftId)
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    getShiftFromService(service: Service) {
        if (service.stopInfo && service.stopInfo.shiftId) {
            return this.model.getShift(service.stopInfo.shiftId);
        }

        if ((!service.stopInfo || !service.stopInfo.shiftId) && service.overrideStartTime) {
            return this.getShiftFromDate(service.overrideStartTime);
        }

        return undefined;
    }

    /**
     * Get the services (sorted) that are scheduled for the shift.
     * 
     * @param shiftId
     */
    getServicesByShiftClone(shiftId: string): any[] {
        return es.util.object.deepCopy(this.model.services
            .filter( s => s.stopInfo?.shiftId === shiftId || (s.overrideStartTime && this.getShiftFromDate(s.overrideStartTime)?.shiftId === shiftId))
        ).sort((a, b) => {
            const startA = a?.stopInfo?.arrTime || a.overrideStartTime;
            const startB = b?.stopInfo?.arrTime || b.overrideStartTime;

            if (startA < startB) {
                return -1;
            }
            if (startA > startB) {
                return 1;
            }
            return 0;
        });
    }

    /**
     * 
     * @param unixSeconds 
     * 
     * @returns 
     */
    getServicesByDateObjRef(unixSeconds: number): any[] {
        const servicesByDate = [];
        const targetDate = dayjs.unix(unixSeconds);
        for (const service of this.model.services) {
            const startTime = this.model.getServiceStartTime(service);
            if (!startTime) {
                continue;
            }

            const dayjsStartTime = dayjs.unix(startTime);
            if (dayjsStartTime.isAfter(targetDate.startOf("day")) && dayjsStartTime.isBefore(targetDate.endOf("day"))) {
                servicesByDate.push(service);
            }
        }

        servicesByDate.sort((s1, s2) => this.model.getServiceStartTime(s1) - this.model.getServiceStartTime(s2));
        return servicesByDate;
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    getServiceStartTime(service): number|null {
        return this.model.getServiceStartTime(service);
    }

    /**
     * Get the services with their corresponding feature (sorted) that are scheduled for the shift.
     * @param shiftId
     * TODO: not in use. remove?
     */
    getServicesWithFeatureByShift(shiftId: string): any[] {
        return this.schedule.getServicesWithFeature()
            .filter( s => s.stopInfo?.shiftId === shiftId || (s.overrideStartTime && this.getShiftFromDate(s.overrideStartTime)?.shiftId === shiftId))
        .sort(function(a, b) {
            const startA = a?.stopInfo.arrTime || a.overrideStartTime;
            const startB = b?.stopInfo.arrTime || b.overrideStartTime;

            if (startA < startB) {
                return -1;
            }
            if (startA > startB) {
                return 1;
            }
            return 0;
        });
    }

    /**
     * Convenience method to generate a gh address obj from a feat (that must have a location obviously)
     * @param feat
     * @returns {{streetHint: null, locationId: string, name, lon: *, lat: *}}
     */
    getAddressFromFeat(feat) {
        const lonLatPnt = [feat.geom[1], feat.geom[2]];

        return {
            locationId: feat.layerViewId + "_" + feat.id, // Specifies the id of the location - in eSpatial's case, this should be layerViewId_featureId
            lon: lonLatPnt[0],
            lat: lonLatPnt[1],
            name: feat.label, // Name of location
            streetHint: null
        };
    }

    /**
     * 
     * @param layerViewId 
     * @param featureId 
     * 
     * @returns 
     */
    isFeatureSchedule(layerViewId: number, featureId: number): boolean {
        let services = this.model.getLocationMappingObjRef().get(layerViewId, featureId);
        return services !== null && services.length > 0;
    }

    /**
     * 
     * @param day 
     * 
     * @returns 
     */
    getDayOfWeek(day: number): string {
        //const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        return daysOfWeek[day];
    }

    /**
     * Checks if a given date is inside the time frame in the settings
     * @param date to check
     * @param checkAvailability if true take into account the start/finish times and dayAvailability for each date. If false, simply check if it within the start/end days
     * @returns {*}
     */
    isDateInsideScheduleTimeFrame(date, checkAvailability = false): boolean {
        if (Number.isInteger(date)) {
            date = new Date(date);
        }

        const dayjsDateObj = dayjs(date);
        if (checkAvailability) {
            const shifts = this.schedule.getShiftsObjRef();
            const shiftInsideDate = _.find(shifts, s =>{
                const shiftStartTime = dayjs.unix(s.earliestStart);
                const shiftFinishTime = dayjs.unix(s.latestEnd);
                return (dayjsDateObj.isAfter(shiftStartTime) || dayjsDateObj.isSame(shiftStartTime)) && (dayjsDateObj.isBefore(shiftFinishTime) || dayjsDateObj.isSame(shiftFinishTime));
            });

            return shiftInsideDate !== undefined;
        }
        
        const scheduleStartTime = dayjs.unix(this.schedule.getSettingsObjRef().startDate);
        const scheduleFinishTime = dayjs.unix(this.schedule.getSettingsObjRef().finishDate).add(23, "hour").add(59, "minute");
        return (dayjsDateObj.isAfter(scheduleStartTime) || dayjsDateObj.isSame(scheduleStartTime)) && (dayjsDateObj.isBefore(scheduleFinishTime) || dayjsDateObj.isSame(scheduleFinishTime));
    }

    /**
     * 
     * @param date 
     * @param availability 
     * 
     * @returns 
     */
    isDayAvailable(date, availability?: any): boolean {
        const dateDayjsObj = dayjs(date);
        let index = dateDayjsObj.day() - 1;

        if (index < 0) {
            index = 6;
        }

        if (!availability) {
            availability = this.schedule.getSettingsObjRef().dayAvailability;
        }

        return availability.charAt(index) === "1";
    }

    /**
     * 
     * @param criteria 
     * @param singleLayerViewId 
     * 
     * @returns 
     */
    getFeaturesRequest(criteria, singleLayerViewId: number): Promise<any> {
        let stylersDfd = this.layerModule.list()
            .filter((lvm) => lvm.isEnabled() && lvm.getGeomType() === 'POINT')
            .map((lvm) => {
                return es.map.vector.style.buildStyler(lvm.getLayerView()).then((styler) => {
                    const ret = {};
                    ret[lvm.getId()] = styler;

                    return ret;
                });
            });

        stylersDfd = Promise.all(stylersDfd).then((args) => {
            return args.reduce(function(a, styler) {
                let k = Object.keys(styler)[0];
                a[k] = styler[k];

                return a;
            }, {});
        });

        const featureViewModel = this.layerModule.getFeatureViewModel();
        return Promise.all([
            featureViewModel.getRequest(this.baseMapModule, true),
            stylersDfd
        ]).then(([request, stylers]) => {
            const layers = request.layers;
            const pointLayerIds = [];
            for (let l = layers.length; l-- > 0;) {
                let layer = layers[l];
                if (this.layerModule.getById(layer.id).getGeomType() === "POINT" && (!singleLayerViewId || layer.id === singleLayerViewId)) {
                    pointLayerIds.push(layer.id);
                    let hideFeaturesCriteria = es.map.vector.style.getHideFeaturesCriteria(stylers[layer.id]);
                    layer.criteria = es.util.criteria.and(layer.criteria, criteria, hideFeaturesCriteria);
                } else {
                    layer.criteria = {$type : "None"};
                }
            }

            request.format = {
                $type: "JsonFormat",
                getAttrs: true,
                includeInternalAttrs: pointLayerIds,
                offset: 0,
                length: MAX_SELECT_LIMIT
            };

            return request;
        });
    }

    /**
     * 
     * @param startDate 
     * @param endDate 
     * @param dayAvailability 
     * 
     * @returns 
     */
    getNumScheduledDaysInRange(startDate, endDate, dayAvailability): number {
        let numDaysInSchedule = 0;
        if(Number.isInteger(startDate)) {
            startDate = dayjs.unix(startDate);
        }
        if(Number.isInteger(endDate)) {
            endDate = dayjs.unix(endDate);
        }

        let date = dayjs(startDate);
        endDate = dayjs(endDate);

        for (date; date <= endDate; date = date.add(1, "day")) {
            if (this.isDayAvailable(date, dayAvailability)) {
                numDaysInSchedule++;
                // Just a safeguard so this method won't take too long.
                if (numDaysInSchedule > 100) {
                    break;
                }
            }
        }

        return numDaysInSchedule;
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    getStopNumberFromService(service: Service): number|undefined {
        if (this.schedule.isServiceAnyStartOrEnd(service)) {
            return undefined;
        }

        if (service.stopInfo?.stopNo !== undefined) {
            return service.stopInfo.stopNo;
        }

        if (!service.overrideStartTime) {
            return undefined;
        }

        const shift = this.getShiftFromService(service);
        if (!shift && this.model.shifts.length > 0) {
            return undefined;
        }

        const services = shift ? this.getServicesByShiftClone(shift.shiftId) : this.getServicesByDateObjRef(this.model.getServiceStartTime(service));
        const index = services
            .filter(s => !this.schedule.isServiceBreak(s))
            .filter(s => !this.schedule.isServiceAnyStartOrEnd(s))
            .findIndex(s => s.id === service.id);

        if (index >= 0) {
            return index + 1;
        }
    }

    /**
     * Returns the features on the Address point Layer that match the NAME, ADDRESS, and GEOM of the points given
     * @param points [
     * {
     *  name: "bla",
     *  address: "bla",
     *  lonLat: [-8.154, 40.6566]
     * }]
     * @returns {Promise<{matchedFeatures: Map<Number, Object>, unmatchedPoints: []}>}
     */
    getAddressPointsOnCoordinates(points): JQueryDeferred<{matchedFeatures: Map<Number, Object>, unmatchedPoints: []}> {
        const featureViewModel = this.layerModule.getFeatureViewModel();
        const addressPointLayerViewId = featureViewModel.getLayersByType("MapScopeCustomPointLayerView")[0].getLayerView().id;

        return es.api.feature.list(addressPointLayerViewId).then(features => {
            const matchedFeatures = new Map();
            const unmatchedPoints = [];

            for(let [index, point] of points.entries()){
                unmatchedPoints.push(point);
                for(let feat of features){
                    if (point.name === feat.attrs.NAME && point.address == feat.attrs.ADDRESS
                        && (feat.geom == undefined && point.lonLat == undefined || ( point.lonLat != undefined && _.isEqual(feat.geom, ["PT",...point.lonLat])))) {
                        matchedFeatures.set(index, feat);
                        unmatchedPoints.pop();
                        break;
                    }
                }
            }
            return {matchedFeatures, unmatchedPoints}
        });
    }

    /**
     * Checks if the overrideStartTime passed is inside the Schedule Start/End Days, Shift Start/End Time and Travel Days
     * Displays an alert with the invalid message
     * @param overrideStartTime Timestamp UNIX
     * @returns {boolean}
     */
    isOverrideStartTimeValid(overrideStartTime: number): boolean {
        const settings = this.schedule.getSettingsObjRef();
        const settingsStartDate = settings.startDate;
        const settingsFinishDate = settings.finishDate;
        const shiftStartTime = dayjs().startOf("d").add(settings.shiftStartTime, "seconds");
        const shiftFinishTime = dayjs().startOf("d").add(settings.shiftFinishTime, "seconds");

        const startDateTime = dayjs.unix(overrideStartTime);

        if (!this.isDateInsideScheduleTimeFrame(startDateTime, true)) {
            es.alert.alertOk($i18n("_Scheduler_Ivalid_Lock_Stop_Date_Msg", [
                dayjs.unix(settingsStartDate).format("YYYY-MM-DD"),
                dayjs.unix(settingsFinishDate).format("YYYY-MM-DD"),
                shiftStartTime.format("HH:mm"),
                shiftFinishTime.format("HH:mm")
            ]));
            return false;
        }

        return true;
    }

    /**
     * Checks if the duration is valid.
     * Displays an alert with the invalid message
     * @param duration Minutes
     * 
     * @returns {boolean}
     */
    isDurationValid(duration: number): boolean {
        if (duration < 5 || duration > 240) {
            es.alert.alertOk($i18n("_Scheduler_Invalid_Stop_Duration"));
            return false;
        }
        
        return true;
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    getStartTime(service): number {
        if(service.hasOwnProperty('stopInfo')) {
            return service['stopInfo']['arrTime'];
        }
        
        if (service.hasOwnProperty("overrideStartTime")) {
            return service['overrideStartTime'];
        }

        return 0;
    }
}
